import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { useAuthContext, useWorkspaceContext } from '@repo/hooks';
import { getPlanType } from '../ManagePlan';
import {
  Section,
  SettingsBoxesWrapper,
  SettingsSectionBox,
  SettingsSectionRow,
  SettingToggle,
} from '@views/Settings/components';
import SVGWrapper from '@repo/icons/SVGWrapper';

import styles from './Aura.module.scss';
import { CSSTransition } from 'react-transition-group';
import { Select } from '@repo/components/forms';
import AuraLogo from './AuraLogo';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAuraSettings,
  updateAuraSettings,
  upgradePlanAndEnableAura,
} from '@repo/redux/actions/userSettings';
import { AuraFreshnessInterval, selectAuraSettings } from '@repo/redux/reducers/user/aura';
import { useTheme } from '@repo/themes';
import { LoaderIcon } from '@repo/icons';

const Digit = ({ digit, className }: { digit: string; className?: string }) => {
  const [theme] = useTheme();
  const themedFillOverride = theme === 'light' ? 'url(#lightModeDigitLG)' : undefined;

  return (
    <SVGWrapper
      viewBox="0 0 13 26"
      width="13"
      height="26"
      className={cn(styles.digit, styles[`digit-${digit}`], className)}
      customColor="#FC9C85"
    >
      <defs>
        <linearGradient
          x1="50%"
          y1="0%"
          x2="50%"
          y2="100%"
          id="lightModeDigitLG"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FF6600" offset="0%"></stop>
          <stop stopColor="#FF7300" offset="67%"></stop>
          <stop stopColor="#FF9900" offset="100%"></stop>
        </linearGradient>
      </defs>
      <path
        fill={themedFillOverride}
        className={styles.topLeft}
        d="M0.256,12.488V1.76L2.848,4.088V10.04L0.256,12.488Z"
      />
      <path
        fill={themedFillOverride}
        className={styles.topRight}
        d="M12.472,1.76V12.488L9.88,10.16V4.232L12.472,1.76Z"
      />
      <path
        fill={themedFillOverride}
        className={styles.top}
        d="M1.024,0.728H11.752L9.376,3.32H3.496L1.024,0.728Z"
      />
      <path
        fill={themedFillOverride}
        className={styles.ctr}
        d="M3.112,14.192A68.996,68.996,0,0,0,1.408,12.872L3.112,11.6H9.664L11.344,12.872L9.664,14.192H3.112Z"
      />
      <path
        fill={themedFillOverride}
        className={styles.btmLeft}
        d="M0.328,13.328L2.92,15.824V21.704L0.328,24.056V13.328Z"
      />
      <path
        fill={themedFillOverride}
        className={styles.btmRight}
        d="M12.544,24.056L9.952,21.584V15.68L12.544,13.328V24.056Z"
      />
      <path
        fill={themedFillOverride}
        className={styles.btm}
        d="M1.048,25.112L3.52,22.52H9.448L11.776,25.112H1.048Z"
      />
    </SVGWrapper>
  );
};

const CountDisplay = ({ count }: { count: number }) => {
  const [theme] = useTheme();
  let digits = String(count).split('');

  if (digits.length < 4) {
    digits = new Array(4 - digits.length).fill('padding').concat(digits);
  }

  return (
    <div className={styles.countDisplay}>
      {digits.map((digit, index) => (
        <div key={index} className={styles.digitWrapper}>
          <Digit digit={digit} />
          {digit !== 'padding' && theme !== 'light' && (
            <Digit digit={digit} className={styles.digitGlow} />
          )}
        </div>
      ))}
    </div>
  );
};

const billingLimitOptions = [
  { value: false, label: 'Unlimited' },
  { value: true, label: 'Limited' },
];

const billingLimitOptionsMap = billingLimitOptions.reduce(
  (opts, opt) => ({ ...opts, [String(opt.value)]: opt }),
  {},
);

const freshnessOptions: { value: AuraFreshnessInterval; label: string }[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'biannually', label: 'Twice a year' },
  { value: 'annually', label: 'Annually' },
];

const freshnessOptionsMap = freshnessOptions.reduce(
  (opts, opt) => ({ ...opts, [opt.value]: opt }),
  {},
);

export default function AuraSettings() {
  const dispatch = useDispatch();
  const authCtx = useAuthContext();
  const workspaceCtx = useWorkspaceContext();
  const planType = useRef(getPlanType(authCtx));
  const userIsAdmin = useRef(workspaceCtx.activeWorkspace && workspaceCtx.userIsAdmin);
  const auraSettings = useSelector(selectAuraSettings);
  const billingLimit = billingLimitOptionsMap[String(auraSettings.is_limited)];
  const freshness = freshnessOptionsMap[auraSettings.interval];
  const isOnTrialPlan = authCtx.getIsOnTrialPlan();

  const handleEnableAura = () => {
    if (!isOnTrialPlan) {
      dispatch(updateAuraSettings({ is_enabled: !auraSettings.is_enabled }));
      return;
    }

    window.app.confirm(
      {
        title: 'Upgrade to our paid plan to enable Aura',
        message:
          'Hi, you’re currently on a free trial of Clay for Teams, enabling Aura enrichment requires us to charge your card on file.',
        actions: [{ title: 'Proceed', value: 'upgrade', variant: 'primary' }],
      },
      (action?: 'upgrade') => {
        if (action === 'upgrade') {
          dispatch(upgradePlanAndEnableAura());
        }
      },
    );
  };

  let beforeToggle = null;
  if (auraSettings.enabling) {
    beforeToggle = <LoaderIcon className={styles.loader} />;
  } else if (auraSettings.error) {
    beforeToggle = <span className={styles.errorMsg}>Please try again or contact support</span>;
  }

  useEffect(() => {
    dispatch(fetchAuraSettings());
  }, []);

  useEffect(() => {
    if (auraSettings.is_enabled && !auraSettings.enabling && isOnTrialPlan) {
      authCtx.refreshSubscriptions();
    }
  }, [auraSettings.is_enabled, auraSettings.enabling, isOnTrialPlan]);

  if (!userIsAdmin.current || planType.current !== 'teams') {
    return null;
  }

  return (
    <Section>
      <SettingsBoxesWrapper>
        <SettingsSectionBox
          className={cn(styles.auraIntroBox, !auraSettings.is_enabled && styles.disabled)}
        >
          <SettingsSectionRow className={styles.auraIntro}>
            <div className={styles.logo}>
              <AuraLogo />
            </div>
            <div className={styles.title}>
              Aura enrichment
              <CSSTransition
                in={!auraSettings.is_enabled}
                classNames={{
                  enter: styles.boxEnter,
                  enterActive: styles.boxEnterActive,
                  exit: styles.boxExit,
                  exitActive: styles.boxExitActive,
                }}
                timeout={150}
                unmountOnExit
              >
                <div className={styles.freeTierBadge}>Free Tier</div>
              </CSSTransition>
            </div>
            <p>
              Aura is a premium enrichment layer on top of Clay's built-in service, built for teams
              who need richer insights, finer control, and more frequent updates on their network.
            </p>
          </SettingsSectionRow>
          <SettingsSectionRow className={styles.row}>
            <SettingToggle
              title="Enable Aura enrichment"
              checked={auraSettings.is_enabled}
              name="aura_enrichment"
              onChange={handleEnableAura}
              disabled={auraSettings.enabling}
              beforeToggle={beforeToggle}
            />
          </SettingsSectionRow>
        </SettingsSectionBox>

        <CSSTransition
          in={!!auraSettings.is_enabled && !auraSettings.enabling}
          classNames={{
            enter: styles.boxEnter,
            enterActive: styles.boxEnterActive,
            exit: styles.boxExit,
            exitActive: styles.boxExitActive,
          }}
          timeout={150}
          unmountOnExit
        >
          <SettingsSectionBox className={styles.auraSettings}>
            <SettingsSectionRow className={styles.refreshCount}>
              <div>
                <div className={styles.refreshRow}>
                  <span>Profile refreshes</span>
                  <span className={styles.dateSpan}>
                    {auraSettings.start_date.format('MMM d')} -{' '}
                    {auraSettings.end_date.format('MMM d')}
                  </span>
                </div>
                <div className={styles.refreshRow}>
                  <span className={styles.subtext}>
                    <b>0</b> refreshed profiles
                  </span>
                  <span className={styles.subtext}>
                    <b>{auraSettings.dollarSpend}</b>{' '}
                    {billingLimit?.value ? (
                      <>
                        used of <b>${auraSettings.dollarLimit || 0}</b>
                      </>
                    ) : (
                      'spent'
                    )}
                  </span>
                </div>
              </div>
              <div>
                <CountDisplay count={auraSettings.count} />
              </div>
            </SettingsSectionRow>

            <SettingsSectionRow className={styles.selectRow}>
              Billing limit
              <Select
                className={styles.select}
                options={billingLimitOptions}
                value={billingLimit}
                onChange={(billingLimit) => {
                  dispatch(updateAuraSettings({ is_limited: billingLimit.value }));
                }}
                isInline
                alignMenuRight
              />
            </SettingsSectionRow>
            <CSSTransition
              in={!!billingLimit.value}
              classNames={{
                enter: styles.boxEnter,
                enterActive: styles.boxEnterActive,
                exit: styles.boxExit,
                exitActive: styles.boxExitActive,
              }}
              timeout={150}
              unmountOnExit
            >
              <SettingsSectionRow className={cn(styles.selectRow, styles.billingLimitAmount)}>
                Billing limit amount
                <div className={styles.billingLimitInputContainer}>
                  <input
                    type="text"
                    className={styles.input}
                    value={auraSettings.dollarLimit}
                    onChange={(e) =>
                      dispatch(
                        updateAuraSettings({ dollarLimit: e.target.value.replace(/\D/g, '') }),
                      )
                    }
                    placeholder="$0"
                  />
                  <div
                    className={cn(
                      styles.inputShadowValue,
                      (auraSettings.dollarLimit === '' || auraSettings.dollarLimit === undefined) &&
                        styles.hidden,
                    )}
                  >
                    $<span>{auraSettings.dollarLimit || 0}</span>
                  </div>
                </div>
              </SettingsSectionRow>
            </CSSTransition>
            <SettingsSectionRow className={styles.freshnessRow}>
              Freshness
              <Select
                className={styles.select}
                options={freshnessOptions}
                value={freshness}
                onChange={({ value }) => {
                  dispatch(updateAuraSettings({ interval: value }));
                }}
                isInline
                alignMenuRight
                noMenuWrap
              />
              <p>
                Set how often Aura enrichment updates profiles to reveal new jobs, moves, and
                milestones—giving you more timely, relevant context.
              </p>
            </SettingsSectionRow>
          </SettingsSectionBox>
        </CSSTransition>
      </SettingsBoxesWrapper>
    </Section>
  );
}
